import React, { useState, useEffect } from 'react';
import { CheckCircle, Clock, XCircle, RotateCcw } from 'lucide-react';
import { DeploymentRequest } from '../../types';
import Avatar from '../common/Avatar';

interface ApprovalTimelineProps {
  deployment: DeploymentRequest;
}

const ApprovalTimeline: React.FC<ApprovalTimelineProps> = ({ deployment }) => {
  const [userCache, setUserCache] = useState<Map<string, any>>(new Map());

  // Fetch user details for TL/PM IDs if needed
  useEffect(() => {
    const fetchUserNames = async () => {
      try {
        // Collect all TL and PM IDs that are strings (not objects)
        const userIds = new Set<string>();
        
        if (Array.isArray(deployment.tlApprovals)) {
          deployment.tlApprovals.forEach((a: any) => {
            if (typeof a.tl === 'string') {
              userIds.add(a.tl);
            }
          });
        }
        
        if (Array.isArray(deployment.pmApprovals)) {
          deployment.pmApprovals.forEach((a: any) => {
            if (typeof a.pm === 'string') {
              userIds.add(a.pm);
            }
          });
        }

        // If no IDs need fetching, return early
        if (userIds.size === 0) return;

        // Fetch user data from the API
        const response = await fetch('/api/users');
        if (!response.ok) throw new Error('Failed to fetch users');
        const users = await response.json();

        // Create a cache of users by ID
        const cache = new Map();
        users.forEach((user: any) => {
          cache.set(user._id, user);
          cache.set(user.id, user);
        });

        setUserCache(cache);
      } catch (err) {
        console.error('Error fetching user names:', err);
      }
    };

    fetchUserNames();
  }, [deployment]);

  // Helper function to get user name from ID or object
  const getUserName = (userRef: any, fallback: string): string => {
    if (!userRef) return fallback;

    // If it's an object with name
    if (typeof userRef === 'object' && userRef.name) {
      return userRef.name.split(' ')[0];
    }

    // If it's an object with username
    if (typeof userRef === 'object' && userRef.username) {
      return userRef.username;
    }

    // If it's a string ID, try to look it up
    if (typeof userRef === 'string') {
      const user = userCache.get(userRef);
      if (user && user.name) {
        return user.name.split(' ')[0];
      }
      if (user && user.username) {
        return user.username;
      }
      // Return shortened ID as last resort
      return userRef.substring(0, 8);
    }

    return fallback;
  };

  // Mirrors getUserName's lookup, but returns the id for Avatar rather than a display name.
  const getUserId = (userRef: any): string | undefined => {
    if (!userRef) return undefined;
    if (typeof userRef === 'object') return userRef.id || userRef._id;
    if (typeof userRef === 'string') return userRef;
    return undefined;
  };

  // Determine status of each stage from the actual approval arrays + status,
  // not from status alone — a rejected request used to render TL+PM as green
  // "approved" because its status simply wasn't 'pending'.
  type StageStatus = 'approved' | 'completed' | 'pending' | 'rejected' | 'rolled_back' | 'skipped';
  const status = deployment.status as string;
  const tlList: any[] = Array.isArray(deployment.tlApprovals) ? deployment.tlApprovals : [];
  const pmList: any[] = Array.isArray(deployment.pmApprovals) ? deployment.pmApprovals : [];
  const dep: any = deployment.deployment || {};
  const rejection: any = (deployment as any).rejection || {};
  const rollback: any = (deployment as any).rollback || {};
  // A normally-deployed (non-emergency) request necessarily passed both
  // approval stages, even if legacy records lack the per-person flags.
  const passedApprovals = ['deployed', 'rolled_back'].includes(status) && !dep.isEmergency;
  const tlAllApproved = (tlList.length > 0 && tlList.every(a => a.approved)) ||
    ['tl_approved', 'pm_approved'].includes(status) || passedApprovals;
  const pmAllApproved = (pmList.length > 0 && pmList.every(a => a.approved)) ||
    status === 'pm_approved' || passedApprovals;
  const isLive = !!dep.deployedAt;

  // Which stage the rejection happened at: prefer the rejecter's role, else
  // the first stage that hadn't fully approved yet.
  const rejectedRole = String(rejection.rejectedBy?.role || '').toLowerCase();
  const rejectedStage: 'tl' | 'pm' | 'deployment' | null = status !== 'rejected' ? null
    : rejectedRole === 'tl' ? 'tl'
    : rejectedRole === 'pm' ? 'pm'
    : rejectedRole === 'devops' ? 'deployment'
    : !tlAllApproved ? 'tl' : !pmAllApproved ? 'pm' : 'deployment';

  const stageStatus = (id: 'tl' | 'pm' | 'deployment', approved: boolean): StageStatus => {
    if (rejectedStage) {
      const order = ['tl', 'pm', 'deployment'];
      const cmp = order.indexOf(id) - order.indexOf(rejectedStage);
      if (cmp === 0) return 'rejected';
      if (cmp > 0) return 'skipped';
      return 'approved';
    }
    return approved ? 'approved' : 'pending';
  };

  const tlStatus = stageStatus('tl', tlAllApproved);
  const pmStatus = stageStatus('pm', pmAllApproved);
  const deploymentStatus: StageStatus = status === 'rolled_back' ? 'rolled_back'
    : rejectedStage ? stageStatus('deployment', false)
    : (status === 'deployed' || isLive) ? 'completed' : 'pending';

  const stages = [
    {
      id: 'tl',
      title: 'TL Approval',
      status: tlStatus,
      approvals: deployment.tlApprovals || [],
    },
    {
      id: 'pm',
      title: 'PM Approval',
      status: pmStatus,
      approvals: deployment.pmApprovals || [],
    },
    {
      id: 'deployment',
      title: 'Deployment',
      status: deploymentStatus,
      deployment: deployment.deployment,
      devopsAssignees: (deployment as any).devopsAssignees || [],
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
      case 'completed':
        return {
          bg: 'bg-emerald-50',
          border: 'border-emerald-400',
          text: 'text-emerald-700',
          icon: 'text-emerald-600',
          line: 'bg-emerald-400'
        };
      case 'rejected':
        return {
          bg: 'bg-red-50',
          border: 'border-red-400',
          text: 'text-red-700',
          icon: 'text-red-600',
          line: 'bg-red-300'
        };
      case 'rolled_back':
        return {
          bg: 'bg-orange-50',
          border: 'border-orange-400',
          text: 'text-orange-700',
          icon: 'text-orange-600',
          line: 'bg-orange-300'
        };
      case 'pending':
        return {
          bg: 'bg-gray-50',
          border: 'border-gray-300',
          text: 'text-gray-600',
          icon: 'text-gray-400',
          line: 'bg-gray-300'
        };
      default:
        return {
          bg: 'bg-gray-50',
          border: 'border-gray-300',
          text: 'text-gray-600',
          icon: 'text-gray-400',
          line: 'bg-gray-300'
        };
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="bg-gray-50 px-4 py-3 rounded-md border border-gray-200">
      <div className="flex items-center justify-between">
        {stages.map((stage, index) => {
          const colors = getStatusColor(stage.status);
          const isCompleted = stage.status === 'approved' || stage.status === 'completed';
          // Rejected/skipped/rolled-back stages aren't waiting on anyone.
          const isWaiting = stage.status === 'pending';

          return (
            <React.Fragment key={stage.id}>
              {/* Stage */}
              <div className="flex items-center gap-2">
                {/* Icon */}
                <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${colors.bg} ${colors.border}`}>
                  {isCompleted ? (
                    <CheckCircle className={`w-4 h-4 ${colors.icon}`} />
                  ) : stage.status === 'rejected' ? (
                    <XCircle className={`w-4 h-4 ${colors.icon}`} />
                  ) : stage.status === 'rolled_back' ? (
                    <RotateCcw className={`w-4 h-4 ${colors.icon}`} />
                  ) : (
                    <Clock className={`w-4 h-4 ${colors.icon}`} />
                  )}
                </div>
                
                {/* Stage info */}
                <div>
                  <div className="text-xs font-semibold text-gray-900">{stage.title}</div>
                  {stage.id === 'tl' && stage.approvals && stage.approvals.length > 0 && (
                    <>
                      {stage.approvals.map((approval: any, idx: number) => {
                        if (approval.approved && approval.approvedAt) {
                          const tlName = getUserName(approval.tl, 'TL');
                          return (
                            <div key={idx} className="flex items-center gap-1 text-xs text-gray-600">
                              <Avatar name={tlName} userId={getUserId(approval.tl)} size="xs" />
                              {tlName} - {formatDate(approval.approvedAt)}
                            </div>
                          );
                        }
                        return null;
                      })}
                      {/* Show pending TLs */}
                      {isWaiting && stage.approvals.some((a: any) => !a.approved) && (
                        <div className={`text-xs ${colors.text}`}>
                          Pending: {stage.approvals
                            .filter((a: any) => !a.approved)
                            .map((a: any) => getUserName(a.tl, 'TL'))
                            .join(', ')}
                        </div>
                      )}
                    </>
                  )}
                  {stage.id === 'pm' && stage.approvals && stage.approvals.length > 0 && (
                    <>
                      {stage.approvals.map((approval: any, idx: number) => {
                        if (approval.approved && approval.approvedAt) {
                          const pmName = getUserName(approval.pm, 'PM');
                          return (
                            <div key={idx} className="flex items-center gap-1 text-xs text-gray-600">
                              <Avatar name={pmName} userId={getUserId(approval.pm)} size="xs" />
                              {pmName} - {formatDate(approval.approvedAt)}
                            </div>
                          );
                        }
                        return null;
                      })}
                      {/* Show pending PMs */}
                      {isWaiting && stage.approvals.some((a: any) => !a.approved) && (
                        <div className={`text-xs ${colors.text}`}>
                          Pending: {stage.approvals
                            .filter((a: any) => !a.approved)
                            .map((a: any) => getUserName(a.pm, 'PM'))
                            .join(', ')}
                        </div>
                      )}
                    </>
                  )}
                  {stage.id === 'deployment' && stage.deployment && stage.deployment.deployedBy && (
                    <div className="flex items-center gap-1 text-xs text-gray-600">
                      <Avatar name={stage.deployment.deployedBy.name} userId={getUserId(stage.deployment.deployedBy)} size="xs" />
                      {(stage.deployment as any).isEmergency && '🚨 '}
                      {stage.deployment.deployedBy.name?.split(' ')[0] || 'DevOps'} - {stage.deployment.deployedAt ? formatDate(stage.deployment.deployedAt) : ''}
                    </div>
                  )}
                  {/* Skip the "still pending" line once it's already live via
                      emergency deploy — it's not waiting on DevOps anymore,
                      just on TL/PM sign-off shown in the stages above. */}
                  {isWaiting && stage.id === 'deployment' && !stage.deployment?.deployedBy && (
                    <div className={`text-xs ${colors.text}`}>
                      {stage.devopsAssignees && stage.devopsAssignees.length > 0
                        ? `Pending: ${stage.devopsAssignees.map((d: any) => getUserName(d, 'DevOps')).join(', ')}`
                        : 'Pending'}
                    </div>
                  )}
                  {isWaiting && stage.id === 'tl' && (!stage.approvals || stage.approvals.length === 0) && (
                    <div className={`text-xs ${colors.text}`}>Pending</div>
                  )}
                  {isWaiting && stage.id === 'pm' && (!stage.approvals || stage.approvals.length === 0) && (
                    <div className={`text-xs ${colors.text}`}>Pending</div>
                  )}
                  {stage.status === 'rejected' && (
                    <div className={`text-xs font-medium ${colors.text}`} title={rejection.reason || undefined}>
                      Rejected{rejection.rejectedBy?.name ? ` by ${String(rejection.rejectedBy.name).split(' ')[0]}` : ''}
                      {rejection.rejectedAt ? ` - ${formatDate(rejection.rejectedAt)}` : ''}
                    </div>
                  )}
                  {stage.status === 'skipped' && (
                    <div className="text-xs text-gray-400">—</div>
                  )}
                  {stage.status === 'rolled_back' && (
                    <div className={`text-xs font-medium ${colors.text}`} title={rollback.reason || undefined}>
                      Rolled back{rollback.rolledBackBy?.name ? ` by ${String(rollback.rolledBackBy.name).split(' ')[0]}` : ''}
                      {rollback.rolledBackAt ? ` - ${formatDate(rollback.rolledBackAt)}` : ''}
                    </div>
                  )}
                </div>
              </div>

              {/* Connecting line */}
              {index < stages.length - 1 && (
                <div className={`flex-1 h-0.5 mx-3 ${colors.line}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default ApprovalTimeline;
